import { supabase, isSupabaseConfigured } from '../lib/supabase';
import contentData from '../data/content.json';

const CONTENT_KEY = 'site_content';
const CONTENT_VERSION = 'v1';

export interface ContentData {
  [key: string]: any;
}

const deepMerge = (defaults: any, overrides: any): any => {
  if (!overrides || typeof overrides !== 'object') return defaults;
  if (!defaults || typeof defaults !== 'object') return overrides;

  const result = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (key in overrides) {
      if (
        typeof defaults[key] === 'object' &&
        defaults[key] !== null &&
        !Array.isArray(defaults[key]) &&
        typeof overrides[key] === 'object' &&
        overrides[key] !== null &&
        !Array.isArray(overrides[key])
      ) {
        result[key] = deepMerge(defaults[key], overrides[key]);
      } else {
        result[key] = overrides[key];
      }
    }
  }
  for (const key of Object.keys(overrides)) {
    if (!(key in defaults)) {
      result[key] = overrides[key];
    }
  }
  return result;
};

export const contentService = {
  async getContent(): Promise<ContentData> {
    if (!isSupabaseConfigured()) {
      return contentData as ContentData;
    }

    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('content')
        .eq('key', CONTENT_KEY)
        .eq('version', CONTENT_VERSION)
        .single();

      if (error) {
        console.warn('Supabase fetch error:', error.message);
        return contentData as ContentData;
      }

      if (data?.content && typeof data.content === 'object') {
        return deepMerge(contentData, data.content) as ContentData;
      }

      return contentData as ContentData;
    } catch (error) {
      console.warn('Failed to fetch from Supabase, using defaults');
      return contentData as ContentData;
    }
  },

  async saveContent(content: ContentData): Promise<void> {
    try {
      const { error } = await supabase
        .from('site_content')
        .upsert({
          key: CONTENT_KEY,
          version: CONTENT_VERSION,
          content: content,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key,version'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving content:', error);
      throw error;
    }
  },

  async resetToDefault(): Promise<void> {
    try {
      const { error } = await supabase
        .from('site_content')
        .delete()
        .eq('key', CONTENT_KEY)
        .eq('version', CONTENT_VERSION);

      if (error) throw error;
    } catch (error) {
      console.error('Error resetting content:', error);
      throw error;
    }
  }
};
